<template>
  <el-dialog :visible.sync="visible" width="92%" :close-on-click-modal="false" append-to-body @closed="onClosed">
    <template slot="title">
      <div class="doc-dialog-header">
        <span class="doc-dialog-title">{{ dialogTitle }}</span>
        <div v-if="readonly && form.id" class="doc-dialog-actions">
          <el-button-group>
            <workflow-doc-buttons biz-type="invoiceSales" :biz-id="form.id" perm="finance:invoiceSales" :approve-status="String(form.approveStatus || '0')" @approve="approve(form, true)" @unapprove="approve(form, false)" />
            <el-button v-if="isAuth('finance:invoiceSales:update') || isAuth('finance:invoiceSales:save')" type="primary" size="small" :disabled="!canEditDoc" @click="editFromView">修改</el-button>
            <el-button v-if="isAuth('finance:invoiceSales:delete') || isAuth('finance:invoiceSales:save')" type="danger" size="small" :disabled="!canEditDoc" @click="deleteFromView">删除</el-button>
          </el-button-group>
        </div>
      </div>
    </template>
    <el-alert v-if="readonly && amountMismatch" class="amount-warning" title="发票明细为空或表头金额与明细合计不一致，请核查历史单据" type="warning" show-icon :closable="false" />
    <el-form ref="dataForm" :model="form" :rules="readonly ? {} : formRules" :disabled="readonly" label-width="110px">
      <el-form-item label="内部单号" prop="code"><el-input v-model="form.code" :disabled="codeReadonly || !!form.id" :placeholder="codeReadonly ? '保存时自动生成' : ''" /></el-form-item>
      <el-form-item label="税务发票号码"><el-input v-model.trim="form.taxInvoiceNumber" maxlength="64" :placeholder="readonly ? '' : '实际票据号码，未取得可留空'" /></el-form-item>
      <el-form-item label="发票日期" prop="bizDate">
        <el-date-picker v-model="form.bizDate" type="date" value-format="yyyy-MM-dd" style="width:100%" />
      </el-form-item>
      <el-form-item label="客户" prop="customerId">
        <el-input :value="form.partnerName" readonly placeholder="请选择客户">
          <el-button v-if="!readonly" slot="append" @click="picker = true">选择</el-button>
        </el-input>
      </el-form-item>
      <el-form-item label="价税合计"><span>{{ lineTotal }}</span></el-form-item>
      <div v-if="!readonly" style="margin-bottom:8px"><el-button type="primary" size="small" :disabled="!form.customerId" @click="linePicker = true">选择应收明细</el-button></div>
      <el-table border :data="form.lines" :header-cell-style="headStyle">
        <el-table-column prop="receivableCode" label="应收单号" width="150" />
        <el-table-column prop="materialCode" label="编码" width="130" />
        <el-table-column prop="materialName" label="名称" min-width="140" />
        <el-table-column label="价税合计" width="160"><template slot-scope="scope"><el-input-number v-model="scope.row.totalMoney" :min="0" :precision="2" size="small" :disabled="readonly" /></template></el-table-column>
        <el-table-column v-if="!readonly" label="操作" width="60" align="center"><template slot-scope="scope"><el-button type="text" size="small" @click="form.lines.splice(scope.$index, 1)">删</el-button></template></el-table-column>
      </el-table>
      <el-form-item label="备注" style="margin-top:12px"><el-input v-model="form.remark" type="textarea" :rows="2" /></el-form-item>
    </el-form>
    <span slot="footer">
      <el-button @click="visible = false">关闭</el-button>
      <el-button v-if="!readonly" type="primary" @click="save">确定</el-button>
    </span>
    <CustomerSelector :visible.sync="picker" @select="onPick" />
    <FinanceInvoiceLineSelector :visible.sync="linePicker" :partner-id="form.customerId" :exclude-id="form.id || ''" :sales="true" @select="onLines" />
  </el-dialog>
</template>

<script>
import WorkflowDocButtons from '../../components/WorkflowDocButtons.vue'
import CustomerSelector from '../../components/CustomerSelector.vue'
import FinanceInvoiceLineSelector from '../../components/FinanceInvoiceLineSelector.vue'
import request from '../../api/request'
import auth from './financeAuth'

export default {
  name: 'InvoiceSalesAddOrUpdate',
  mixins: [auth],
  components: { WorkflowDocButtons, CustomerSelector, FinanceInvoiceLineSelector },
  data() {
    return {
      visible: false,
      readonly: false,
      codeReadonly: false,
      picker: false,
      linePicker: false,
      form: this.blank(),
      rules: {
        code: [{ required: true, message: '内部单号不能为空', trigger: 'blur' }],
        bizDate: [{ required: true, message: '发票日期不能为空', trigger: 'change' }],
        customerId: [{ required: true, message: '客户不能为空', trigger: 'change' }]
      }
    }
  },
  computed: {
    formRules() {
      return this.codeReadonly ? { ...this.rules, code: [] } : this.rules
    },
    dialogTitle() {
      if (this.readonly) return '查看销项发票'
      return this.form.id ? '修改销项发票' : '新增销项发票'
    },
    lineTotal() { return (this.form.lines || []).reduce((s, l) => s + Number(l.totalMoney || 0), 0).toFixed(2) },
    amountMismatch() { return !(this.form.lines || []).length || Math.abs(Number(this.form.amount || 0) - Number(this.lineTotal)) >= 0.01 }
  },
  methods: {
    blank() {
      return { kind: 'invoiceSales', code: '', taxInvoiceNumber: '', bizDate: this.today(), customerId: '', partnerName: '', amount: 0, remark: '', lines: [] }
    },
    resolveCode() {
      request.post('/base/docNumberRule/resolve', { docType: 'invoiceSales' }).then(res => {
        const data = res.data || {}
        this.codeReadonly = !!data.codeReadonly
        if (data.previewCode) this.form.code = data.previewCode
        this.$nextTick(() => this.$refs.dataForm && this.$refs.dataForm.clearValidate('code'))
      }).catch(() => {})
    },
    init(id, readonly) {
      this.visible = true
      this.readonly = !!readonly
      this.codeReadonly = false
      this.picker = false
      this.linePicker = false
      if (!id) {
        this.form = this.blank()
        this.resolveCode()
        this.$nextTick(() => this.$refs.dataForm && this.$refs.dataForm.clearValidate())
        return
      }
      request.get('/finance/doc/info', { params: { kind: 'invoiceSales', id } }).then(res => {
        const row = res.data || {}
        this.form = Object.assign({ kind: 'invoiceSales', lines: [] }, row)
        if (!Array.isArray(this.form.lines)) this.form.lines = []
        this.$nextTick(() => this.$refs.dataForm && this.$refs.dataForm.clearValidate())
      }).catch(() => {})
    },
    onClosed() {
      this.form = this.blank()
      this.readonly = false
    },
    editFromView() {
      if (!this.assertCanEditDoc('edit')) return
      this.readonly = false
    },
    deleteFromView() {
      if (!this.assertCanEditDoc('delete')) return
      this.$confirm('确定删除该销项发票?', '提示', { type: 'warning' }).then(() => {
        request.post('/finance/doc/delete', { kind: 'invoiceSales', id: this.form.id }).then(() => {
          this.$message.success('删除成功')
          this.visible = false
          this.$emit('refreshDataList')
        }).catch(() => {})
      }).catch(() => {})
    },
    onPick(row) { this.form.customerId = row.customerId; this.form.partnerName = row.customerName; this.form.lines = [] },
    onLines(rows) { rows.forEach(row => this.form.lines.push(row)) },
    save() {
      this.$refs.dataForm.validate(valid => {
        if (!valid) return
        if (!this.form.lines.length) { this.$message.warning('请选择应收明细'); return }
        this.form.amount = Number(this.lineTotal)
        request.post('/finance/doc/save', this.form).then(() => {
          this.$message.success('保存成功')
          this.visible = false
          this.$emit('refreshDataList')
        }).catch(() => {})
      })
    },
    approve(row, pass) {
      const url = pass ? '/finance/doc/approve' : '/finance/doc/unapprove'
      this.$confirm(pass ? '确定审核?' : '确定弃审?', '提示', { type: 'warning' }).then(() => {
        request.post(url, { kind: 'invoiceSales', id: row.id }).then(() => {
          this.$message.success(pass ? '审核成功' : '弃审成功')
          if (row && this.form && ((row.id && this.form.id === row.id) || (row.voucherId && this.form.voucherId === row.voucherId))) {
            this.$set(this.form, 'approveStatus', pass ? '2' : '0')
            this.readonly = true
          }
          this.$emit('refreshDataList')
        }).catch(() => {})
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.amount-warning { margin-bottom: 12px; }
.doc-dialog-header { display: flex; align-items: center; justify-content: space-between; padding-right: 28px; }
.doc-dialog-title { font-size: 16px; font-weight: 600; }
</style>
